/* ==========================================================================
   Serio Ludere — password / coming-soon page

   The image sequence itself is pure CSS: both stacks share one document
   timeline, so they stay frame-accurate without a JS clock and keep running
   with scripting disabled. This file only handles the four things CSS cannot:

     1. Repair — a mask layer that fails to load is transparent black, i.e. the
        wordmark disappears rather than degrading. @supports cannot detect
        that, so probe the SVG and fall back to the flat logo if it 404s.
     2. The optional pause control (WCAG 2.2 SC 2.2.2), which is off by default.
     3. Splitting the single "Full name" field into Shopify's first/last pair.
     4. Making constraint-validation failures visible — see bindValidationFeedback.
   ========================================================================== */

(function () {
  'use strict';

  function probeMask(root) {
    var window_ = root.querySelector('.sl-mark__window');
    if (!window_) return;

    // Read the *computed* value off the masked element: the browser has
    // already resolved it to an absolute URL against the right stylesheet
    // base, which a raw custom-property string would not be.
    var style = getComputedStyle(window_);
    var declared = style.maskImage || style.webkitMaskImage || '';

    // No mask support at all — the @supports block already left the flat
    // wordmark showing, so there is nothing to repair.
    if (declared === '' || declared === 'none') return;

    var match = declared.match(/url\(\s*(['"]?)(.*?)\1\s*\)/);
    var url = match ? match[2] : '';

    if (!url) {
      root.classList.add('sl-mask-failed');
      return;
    }

    var probe = new Image();
    probe.onerror = function () {
      root.classList.add('sl-mask-failed');
    };
    probe.src = url;
  }

  function bindMotionToggle(root) {
    var toggle = root.querySelector('.sl-motion-toggle');
    if (!toggle) return;

    var labels = {
      pause: toggle.dataset.labelPause || 'Pause',
      play: toggle.dataset.labelPlay || 'Play'
    };

    // The visible text is the button's accessible name and it names the next
    // action, so no aria-pressed — it would announce the opposite state.
    function render(paused) {
      root.style.setProperty('--sl-play', paused ? 'paused' : 'running');
      toggle.textContent = paused ? labels.play : labels.pause;
    }

    // Respect the OS setting on load, and keep following it live.
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    var paused = reduce.matches;
    render(paused);

    reduce.addEventListener('change', function (event) {
      paused = event.matches;
      render(paused);
    });

    toggle.addEventListener('click', function () {
      paused = !paused;
      render(paused);
    });
  }

  /* --------------------------------------------------------------------------
     Visible validation feedback.

     The signup form gates consent on a `required` checkbox deliberately: it
     keeps the form unsubmittable without consent even with scripting off, so
     submitting IS the consent record.

     The cost is that the browser's own failure path is invisible here. Prestige
     draws .checkbox as a custom control over a transparent native input, and
     the form sits inside an x-drawer. When validation fails, the browser
     abandons the submit and tries to anchor its bubble to a control the visitor
     cannot see — so the button reads as dead: no message, no navigation, no
     request, nothing in the network log.

     `invalid` still fires on every failing control. Catch it, suppress the
     bubble we cannot rely on, and render the browser's own localised
     validationMessage somewhere visible instead.

     Note this does NOT relax the gate: `required` stays in the markup, the
     browser still refuses to submit, and the no-JS guarantee is untouched. The
     only thing that changes is that the refusal is now legible.
     -------------------------------------------------------------------------- */
  function bindValidationFeedback(root) {
    var form = root.querySelector('.sl-signup');
    if (!form) return;

    var notice = null;

    function ensureNotice() {
      if (notice && notice.isConnected) return notice;

      notice = document.createElement('p');
      notice.className = 'sl-signup__notice';
      // role=alert so it is announced the moment it appears, without the
      // visitor having to find it.
      notice.setAttribute('role', 'alert');
      notice.style.cssText = [
        'margin:0 0 0.75rem',
        'padding:0.625rem 0.75rem',
        'border:1px solid currentColor',
        'font-size:0.8125rem',
        'line-height:1.4',
        'text-align:center'
      ].join(';');

      form.insertBefore(notice, form.firstChild);
      return notice;
    }

    function clear() {
      if (notice && notice.isConnected) notice.remove();

      Array.prototype.forEach.call(
        form.querySelectorAll('[aria-invalid="true"]'),
        function (el) {
          el.removeAttribute('aria-invalid');
        }
      );
    }

    // One validation pass fires `invalid` on every failing control in document
    // order. Only the first is worth reporting, so latch — then release on the
    // next tick so the following attempt starts clean.
    var reported = false;

    form.addEventListener(
      'invalid',
      function (event) {
        var field = event.target;

        // Suppress the native bubble. For the consent checkbox it anchors to a
        // control with no visible box, which is the entire bug being fixed.
        event.preventDefault();

        if (reported) return;
        reported = true;
        setTimeout(function () {
          reported = false;
        }, 0);

        field.setAttribute('aria-invalid', 'true');
        ensureNotice().textContent =
          field.validationMessage || 'Please complete this field.';

        // The drawer scrolls independently of the page, so the offending field
        // may well be out of view.
        if (typeof field.scrollIntoView === 'function') {
          field.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }

        // A transparent checkbox still takes focus, and focus is what moves
        // Prestige's custom control into its :focus-visible state.
        try {
          field.focus({ preventScroll: true });
        } catch (error) {
          field.focus();
        }
      },
      true // capture — `invalid` does not bubble
    );

    // Drop the message as soon as the visitor fixes whatever was wrong, so it
    // never lingers next to a field that now passes.
    form.addEventListener('change', clear);
    form.addEventListener('submit', clear);
  }

  /* Shopify stores first and last name separately, but the design asks for a
     single "Full name" field. The visible input is therefore contact[first_name]
     and a hidden contact[last_name] sits next to it: everything after the first
     space is moved across just before the POST.

     Deliberately NOT done on input/blur — only on submit — so the field the
     visitor is looking at never rewrites itself under the cursor. `submit` only
     fires once the form is valid, so a failed validation pass can no longer
     leave the visible field truncated to its first word.

     With scripting off the whole string simply posts as the first name. Untidy,
     but the name is never lost, which is why the visible field carries the real
     first_name attribute rather than being a JS-only proxy. */
  function bindNameSplit(root) {
    var form = root.querySelector('.sl-signup');
    if (!form) return;

    var last = form.querySelector('[data-sl-lastname]');
    var first = form.querySelector('[name="contact[first_name]"]');
    if (!last || !first) return;

    form.addEventListener('submit', function () {
      // Collapse runs of whitespace so "Ada   Lovelace" splits cleanly.
      var full = (first.value || '').trim().replace(/\s+/g, ' ');
      if (!full) return;

      var cut = full.indexOf(' ');
      if (cut === -1) {
        // Mononym, or the visitor gave one name. Leave last_name empty rather
        // than duplicating the first — a duplicated surname is worse data.
        first.value = full;
        last.value = '';
        return;
      }

      first.value = full.slice(0, cut);
      last.value = full.slice(cut + 1);
    });
  }

  /* --------------------------------------------------------------------------
     CAPTCHA wire-up.

     Spam protection is on for this store, so /contact rejects any POST that
     arrives without an hCaptcha token — HTTP 400, "Missing CAPTCHA token".
     Shopify wires that token up lazily: its own script binds hCaptcha on the
     first focusin or change inside the form, and until that async bind lands it
     preventDefault()s every submit and does nothing else. No request, no error,
     no navigation — the button is simply dead, which looks identical to the
     failure bindValidationFeedback above exists to make legible.

     Measured on this page the bind takes ~3s: the provider script from
     cdn.shopify.com, then two hCaptcha frames from newassets.hcaptcha.com. A
     visitor who opens the drawer and types briskly lands inside that window and
     silently loses their first click.

     protect() starts the same bind at page load instead, so those seconds are
     spent while the visitor is still reading the page. The section markup also
     carries data-shopify-captcha="true", which covers the ordinary render; this
     call is what covers a theme-editor re-render, where Shopify's own
     DOMContentLoaded pass has long since run.

     protect() does not itself check whether a form is already wired, so guard on
     Shopify's own marker or the editor would bind a second widget to the same
     form. Going through the documented API deliberately — their docs explicitly
     warn against touching window.hCaptcha.
     -------------------------------------------------------------------------- */
  function bindCaptcha(root) {
    var form = root.querySelector('.sl-signup');
    if (!form) return;

    // Shopify sets this the moment it wires a form, by either route.
    if (form.dataset.cptcha === 'true') return;

    var captcha = window.Shopify && window.Shopify.captcha;
    if (!captcha || typeof captcha.protect !== 'function') return;

    try {
      captcha.protect(form);
    } catch (error) {
      // Best effort. If it throws, Shopify's bind-on-interaction path is still
      // in place and the form is no worse off than it was before.
    }
  }

  function init(root) {
    if (!root || root.dataset.slReady === 'true') return;
    root.dataset.slReady = 'true';
    probeMask(root);
    bindMotionToggle(root);
    // First, because it is much the slowest and nothing else here waits on it.
    bindCaptcha(root);
    // Bind before bindNameSplit so the notice is in place for the first pass.
    bindValidationFeedback(root);
    bindNameSplit(root);
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('.sl-password').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  // Theme editor re-renders the whole section on every setting change.
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
